import { FC, useCallback, useState } from "react";
import { HiOutlinePlusSm } from "react-icons/hi";
import { HiOutlineMinusSmall } from "react-icons/hi2";
import { AiOutlineDelete } from "react-icons/ai";
import { useTrackContext } from "../../../hooks/useTrackContext";
import { updateCartQuantity, dispatchCartUpdates } from "../../../utils/cartOperations";

interface IProps {
  headlessVariantID?: string;
  productTittle: string;
  image: string;
  title: string;
  price: number;
  compareAtPrice: number;
  quantity: number;
  id: string;
  variantTitle?: string;
  productType?: string;
  isComponentUsedForMiniCart?: boolean;
  gameOptions?: { name: string; value: string }[];
}

const CartMobileView: FC<IProps> = ({
  title,
  productTittle,
  price,
  compareAtPrice,
  isComponentUsedForMiniCart,
  productType,
  quantity,
  id,
  variantTitle,
  image,
  headlessVariantID,
  gameOptions,
}) => {
  const [currentQuantity, setCurrentQuantity] = useState<number>(quantity);
  const { dispatch, productRenderedLocation, headlessCartInfo, headlessCartRemoveItem } = useTrackContext();

  // Check if this is a Battle Kart product
  const isBattleKartProduct = title.toLowerCase().includes("battle") || productTittle.toLowerCase().includes("battle") || variantTitle?.toLowerCase().includes("battle");

  const handleQuantityUpdate = useCallback(
    async (id: string, newQuantity: number) => {
      try {
        setCurrentQuantity(newQuantity);
        const result = await updateCartQuantity(id, newQuantity, productRenderedLocation, headlessCartInfo);
        dispatchCartUpdates(dispatch, result.cart, productRenderedLocation);
      } catch (error) {
        console.error("Failed to update cart quantity:", error);
        setCurrentQuantity(quantity);
      }
    },
    [productRenderedLocation, headlessCartInfo, dispatch, quantity]
  );

  const handlePlus = useCallback(
    (id: string) => {
      const newQuantity = currentQuantity + 1;
      handleQuantityUpdate(id, newQuantity);
    },
    [currentQuantity, handleQuantityUpdate]
  );

  const handleMinus = useCallback(
    (id: string) => {
      if (currentQuantity > 1) {
        const newQuantity = currentQuantity - 1;
        handleQuantityUpdate(id, newQuantity);
      }
    },
    [currentQuantity, handleQuantityUpdate]
  );

  const handleRemoveItem = useCallback(
    (itemId: string) => {
      if (productRenderedLocation === "external") {
        dispatch({
          type: "SET_HEADLESS_CART_REMOVE_ITEM",
          payload: {
            headlessCartRemoveItem: {
              ...headlessCartRemoveItem,
              displayCartComfirmationPopup: true,
              ItemId: itemId,
            },
          },
        });
      } else {
        handleQuantityUpdate(itemId, 0);
      }
    },
    [productRenderedLocation, dispatch, headlessCartRemoveItem, handleQuantityUpdate]
  );

  const gameTime = gameOptions
    ?.find((game) => game.name.includes("Race Time") || game.name.includes("Game Time") || game.name.includes("Drive Time"))
    ?.value.replace(/(\d+:\d+)([APap][Mm])/, "$1 $2")
    .toUpperCase();

  return (
    <>
      <div className={`react-cart__wrapper ${isComponentUsedForMiniCart ? "tw-mb-[10px]" : ""}`} data-item-id={productRenderedLocation === "external" ? id : id.split(":")[0]}>
        <div className={`tw-flex tw-items-center ${isComponentUsedForMiniCart ? "tw-justify-between" : "tw-justify-normal"} tw-gap-[20px]`}>
          {isComponentUsedForMiniCart ? (
            <>
              <div className="tw-flex tw-gap-[10px]">
                <div
                  className={`${isComponentUsedForMiniCart ? "tw-w-[120px] tw-h-[120px]" : "tw-w-[100px] tw-h-[100px]"} ${
                    isBattleKartProduct
                      ? "tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)]"
                      : "tw-bg-[linear-gradient(290deg,#F908FF_0%,#D32EFE_8.19%,#37CCFB_87.88%,#05FFFA_100%)]"
                  } tw-p-[2px] tw-rounded-lg tw-overflow-hidden`}
                >
                  <img src={image} alt={title} className="tw-w-full tw-h-full tw-rounded-md" />
                </div>
                <div>
                  <div>
                    <span className={`tw-font-[600] tw-text-[14px] ${isComponentUsedForMiniCart ? "tw-text-[#000]" : "tw-text-[#fff]"} tw-block`}>
                      {productTittle === "Pre-Paid Arcade Card" ? productTittle + " - " + variantTitle : title.split("|")[0]}
                    </span>

                    {isComponentUsedForMiniCart ? (
                      <div className="tw-grid tw-grid-cols-2 tw-gap-[5px] tw-mt-[5px]">
                        <span className={`tw-text-[12px] tw-py-[2px] tw-px-[8px] tw-font-[500] tw-text-[#555758] tw-bg-[#EBECEF] tw-rounded-[30px] tw-w-fit tw-h-fit`}>
                          {productType === "Main Track" || productType === "Mini Track" || productType === "Virtual Reality"
                            ? productTittle.split("|")[1].includes("Mini Track")
                              ? productTittle.split("|")[1].replace("Mini Track", "Race").trim()
                              : productTittle.split("|")[1].includes("Virtual Reality")
                              ? productTittle.split("|")[1].replace("Virtual Reality", "Race").trim()
                              : productTittle.split("|")[1]
                            : productType}
                        </span>
                        {gameTime && (
                          <>
                            <span className={`tw-text-[12px] tw-py-[2px] tw-px-[8px] tw-font-[500] tw-text-[#555758] tw-bg-[#EBECEF] tw-rounded-[30px] tw-w-fit`}>{gameTime}</span>
                          </>
                        )}
                        {/* {gameType && (
                          <>
                            <span className={`tw-text-[12px] tw-py-[2px] tw-px-[8px] tw-font-[500] tw-text-[#555758] tw-bg-[#EBECEF] tw-rounded-[30px] tw-w-fit tw-mb-[5px]`}>
                              {gameType}
                            </span>
                          </>
                        )} */}
                      </div>
                    ) : (
                      <>
                        <span className={`tw-text-[12px] tw-font-[500] tw-text-[#fff] tw-block`}>
                          {productTittle === "Pre-Paid Arcade Card" ? title : title.split("|")[1].split("-")[0]}
                        </span>
                      </>
                    )}
                    {isComponentUsedForMiniCart && (
                      <>
                        <div className={`tw-flex tw-justify-between tw-mt-[5px]`}>
                          <div className="tw-bg-white tw-rounded-[56px] tw-w-fit tw-border-[1px]">
                            <div className="tw-flex tw-justify-around tw-flex-row tw-items-center tw-gap-[14px] tw-py-[4px] tw-px-[10px]">
                              <span
                                className="tw-bg-transparent tw-focus:tw-outline-none hover:tw-cursor-pointer"
                                onClick={() => handleMinus(productRenderedLocation === "external" ? (headlessVariantID as string) : id)}
                              >
                                <HiOutlineMinusSmall className={`tw-w-[20px] tw-h-[20px] ${currentQuantity <= 1 ? "tw-text-[#EBECEF] tw-cursor-not-allowed" : "tw-text-black"}`} />
                              </span>
                              <span className="tw-text-black tw-text-[18px] tw-font-[500]">{currentQuantity}</span>
                              <span
                                className="tw-bg-transparent tw-focus:tw-outline-none hover:tw-cursor-pointer"
                                onClick={() =>
                                  title.toLocaleLowerCase().includes("mini track") && currentQuantity === 6
                                    ? alert("Mini Track reached max quantity")
                                    : title.toLocaleLowerCase().includes("virtual reality") && currentQuantity === 6
                                    ? alert("Virtual Reality reached max quantity")
                                    : currentQuantity === 16
                                    ? alert("Main Track reached max quantity")
                                    : handlePlus(productRenderedLocation === "external" ? (headlessVariantID as string) : id)
                                }
                              >
                                <HiOutlinePlusSm
                                  className={`tw-w-[20px] tw-h-[20px] ${
                                    (title.toLocaleLowerCase().includes("mini track") && currentQuantity === 6) ||
                                    (title.toLocaleLowerCase().includes("virtual reality") && currentQuantity === 6) ||
                                    currentQuantity === 16
                                      ? "tw-text-[#EBECEF] tw-cursor-not-allowed"
                                      : "tw-text-black"
                                  }`}
                                />
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {!isComponentUsedForMiniCart && (
                    <>
                      <div className="tw-text-[14px] tw-font-[500] tw-flex tw-gap-[10px]">
                        <span className="tw-text-[#C2C0C3]">
                          {" "}
                          ${productRenderedLocation === "external" ? Number(compareAtPrice).toFixed(2) : Number(compareAtPrice / 100).toFixed(2)}
                        </span>
                        <span className="tw-text-[#777] tw-line-through">
                          $
                          {productRenderedLocation === "external"
                            ? Number(compareAtPrice).toFixed(2)
                            : productType === "Main Track"
                            ? "59"
                            : productType === "Mini Track"
                            ? "19"
                            : title.toLowerCase().includes("pre-paid arcade card - 50")
                            ? "135"
                            : title.toLowerCase().includes("pre-paid arcade card - 20")
                            ? "54"
                            : title.toLowerCase().includes("pre-paid arcade card - 10")
                            ? "27"
                            : productType === "Virtual Reality"
                            ? "34"
                            : Number(compareAtPrice / 100).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <div
                className={`tw-w-[100px] tw-h-[100px] ${
                  isBattleKartProduct
                    ? "tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)]"
                    : "tw-bg-[linear-gradient(290deg,#F908FF_0%,#D32EFE_8.19%,#37CCFB_87.88%,#05FFFA_100%)]"
                } tw-p-[2px] tw-rounded-lg tw-overflow-hidden`}
              >
                <img src={image} alt={title} className="tw-w-full tw-h-full tw-rounded-md" />
              </div>
              <div>
                <div>
                  <span className={`tw-font-[600] tw-text-[14px] ${isComponentUsedForMiniCart ? "tw-text-[#000]" : "tw-text-[#fff]"} tw-block`}>
                    {productTittle === "Pre-Paid Arcade Card" ? productTittle + " - " + variantTitle : title.split("|")[0]}
                  </span>
                  <span className={`tw-text-[12px] tw-font-[500] ${isComponentUsedForMiniCart ? "tw-text-[#000]" : "tw-text-[#fff]"} tw-block`}>
                    {productTittle === "Pre-Paid Arcade Card" ? title : title.split("|")[1].split("-")[0]}
                  </span>
                  {gameTime && !isComponentUsedForMiniCart && (
                    <span className="tw-text-[12px] tw-font-[500] tw-text-[#fff] tw-block">
                      {gameTime}
                    </span>
                  )}
                  {isComponentUsedForMiniCart && (
                    <>
                      <div className="tw-flex tw-justify-between tw-my-[20px]">
                        <div className="tw-bg-white tw-rounded-[40.961px] tw-w-fit">
                          <div className="tw-flex tw-justify-around tw-flex-row tw-items-center tw-gap-[14px] tw-py-[4px] tw-px-[10px]">
                            <span
                              className="tw-bg-transparent tw-focus:tw-outline-none hover:tw-cursor-pointer"
                              onClick={() => handleMinus(productRenderedLocation === "external" ? (headlessVariantID as string) : id)}
                            >
                              <HiOutlineMinusSmall className={`tw-w-[20px] tw-h-[20px] ${currentQuantity <= 1 ? "tw-text-[#EBECEF] tw-cursor-not-allowed" : "tw-text-black"}`} />
                            </span>
                            <span className="tw-text-black tw-text-[18px] tw-font-[500]">{currentQuantity}</span>
                            <span
                              className="tw-bg-transparent tw-focus:tw-outline-none hover:tw-cursor-pointer"
                              onClick={() =>
                                title.toLocaleLowerCase().includes("mini track") && currentQuantity === 6
                                  ? alert("Mini Track reached max quantity")
                                  : title.toLocaleLowerCase().includes("virtual reality") && currentQuantity === 6
                                  ? alert("Virtual Reality reached max quantity")
                                  : currentQuantity === 16
                                  ? alert("Main Track reached max quantity")
                                  : handlePlus(productRenderedLocation === "external" ? (headlessVariantID as string) : id)
                              }
                            >
                              <HiOutlinePlusSm
                                className={`tw-w-[20px] tw-h-[20px] ${
                                  (title.toLocaleLowerCase().includes("mini track") && currentQuantity === 6) ||
                                  (title.toLocaleLowerCase().includes("virtual reality") && currentQuantity === 6) ||
                                  currentQuantity === 16
                                    ? "tw-text-[#EBECEF] tw-cursor-not-allowed"
                                    : "tw-text-black"
                                }`}
                              />
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {!isComponentUsedForMiniCart && (
                  <>
                    <div className="tw-text-[14px] tw-font-[500] tw-flex tw-gap-[10px]">
                      <span className="tw-text-[#C2C0C3]">
                        {" "}
                        ${productRenderedLocation === "external" ? Number(compareAtPrice).toFixed(2) : Number(compareAtPrice / 100).toFixed(2)}
                      </span>
                      <span className="tw-text-[#777] tw-line-through">
                        $
                        {productRenderedLocation === "external"
                          ? Number(compareAtPrice).toFixed(2)
                          : productType === "Main Track"
                          ? "59"
                          : productType === "Mini Track"
                          ? "19"
                          : title.toLowerCase().includes("pre-paid arcade card - 50")
                          ? "135"
                          : title.toLowerCase().includes("pre-paid arcade card - 20")
                          ? "54"
                          : title.toLowerCase().includes("pre-paid arcade card - 10")
                          ? "27"
                          : productType === "Virtual Reality"
                          ? "34"
                          : Number(compareAtPrice / 100).toFixed(2)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {isComponentUsedForMiniCart && (
            <>
              <div className="tw-flex tw-gap-[10px] tw-justify-end tw-items-center tw-flex-col">
                <span onClick={() => handleRemoveItem(headlessVariantID as string)} className={`hover:tw-cursor-pointer tw-w-full tw-text-right ${isBattleKartProduct ? "tw-text-[#EA581A]" : "tw-text-[#9D62FE]"}`}>
                  Remove
                </span>
                <div className="tw-text-[14px] tw-font-[700] tw-flex tw-gap-[10px]">
                  <span className="tw-text-[#000000]">${Number(price).toFixed(2)}</span>
                  <span className="tw-text-[#C2C0C3] tw-line-through">${Number(compareAtPrice).toFixed(2)}</span>
                </div>
              </div>
            </>
          )}
        </div>
        {!isComponentUsedForMiniCart && (
          <div className={`tw-flex tw-justify-between tw-my-[20px]`}>
            <>
              <div className="tw-bg-white tw-rounded-[40.961px] tw-w-fit">
                <div className="tw-flex tw-justify-around tw-flex-row tw-items-center tw-gap-[14px] tw-py-[4px] tw-px-[10px]">
                  <button
                    className="tw-bg-transparent tw-focus:tw-outline-none"
                    onClick={() => handleMinus(productRenderedLocation === "external" ? (headlessVariantID as string) : id)}
                  >
                    <HiOutlineMinusSmall className={`tw-w-[20px] tw-h-[20px] ${currentQuantity <= 1 ? "tw-text-[#EBECEF] tw-cursor-not-allowed" : "tw-text-black"}`} />
                  </button>
                  <span className="tw-text-black tw-text-[18px] tw-font-[500]">{currentQuantity}</span>
                  <button
                    className="tw-bg-transparent tw-focus:tw-outline-none"
                    onClick={() =>
                      title.toLocaleLowerCase().includes("mini track") && currentQuantity === 6
                        ? alert("Mini Track reached max quantity")
                        : title.toLocaleLowerCase().includes("virtual reality") && currentQuantity === 6
                        ? alert("Virtual Reality reached max quantity")
                        : currentQuantity === 16
                        ? alert("Main Track reached max quantity")
                        : handlePlus(productRenderedLocation === "external" ? (headlessVariantID as string) : id)
                    }
                  >
                    <HiOutlinePlusSm
                      className={`tw-w-[20px] tw-h-[20px] ${
                        (title.toLocaleLowerCase().includes("mini track") && currentQuantity === 6) ||
                        (title.toLocaleLowerCase().includes("virtual reality") && currentQuantity === 6) ||
                        currentQuantity === 16
                          ? "tw-text-[#EBECEF] tw-cursor-not-allowed"
                          : "tw-text-black"
                      }`}
                    />
                  </button>
                </div>
              </div>
              <div className="tw-flex tw-gap-[23px] tw-items-center">
                <span className="tw-text-[18px] tw-font-[500] tw-text-white">
                  ${productRenderedLocation === "external" ? Number(currentQuantity * price).toFixed(2) : Number((currentQuantity * price) / 100).toFixed(2)}
                </span>
                <AiOutlineDelete
                  onClick={() => handleRemoveItem(productRenderedLocation === "external" ? (headlessVariantID as string) : id)}
                  className={`tw-w-[30px] tw-h-[30px] hover:tw-cursor-pointer ${isBattleKartProduct ? "tw-text-[#EA581A]" : "tw-text-[#9D62FE]"}`}
                />
              </div>
            </>
          </div>
        )}
      </div>
    </>
  );
};

export default CartMobileView;
